import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ArrowRight, BookOpen, Film } from "lucide-react";
import { findBySlug } from "../../data/learningArticles";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";
import VideoCard from "../../components/public/VideoCard";

export default function LearningArticle() {
  const { slug } = useParams();
  const article = findBySlug(slug);

  useDocMeta({
    title: article ? `${article.title} · GovBidIQ Academy` : "Lesson · GovBidIQ Academy",
    description: article ? article.summary : "GovBidIQ Academy lesson.",
    path: `/learning/${slug}`,
  });

  useEffect(() => {
    if (article) track(EVENT.LEARNING_ARTICLE_VIEWED, { slug: article.slug, category: article.category });
  }, [article]);

  if (!article) return <Navigate to="/learning" replace />;

  const related = (article.related || []).map((s) => findBySlug(s)).filter(Boolean);
  const next = findBySlug(article.next_lesson_slug);

  return (
    <div className="max-w-[900px] mx-auto px-6 py-14">
      <Link to="/learning" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 mb-4 inline-flex items-center gap-1.5">
        <BookOpen className="w-3 h-3" /> Back to Academy
      </Link>
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ {article.category}</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight mb-3">{article.title}</h1>
      <div className="flex items-center gap-4 text-[11px] font-mono text-slate-500 mb-8">
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_minutes} min read</span>
        <span>· 2-minute summary available</span>
        <span>· Video coming soon</span>
      </div>

      <div className="border-l-2 border-sky-500/60 pl-4 mb-8 py-2">
        <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-1">/ 2-min Summary (placeholder)</div>
        <p className="text-slate-300 leading-relaxed text-sm">{article.summary}</p>
      </div>

      <article className="space-y-6 mb-10">
        {article.body_sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-display font-bold text-slate-100 text-xl mb-2">{s.heading}</h2>
            <p className="text-slate-300 leading-relaxed">{s.body}</p>
          </section>
        ))}
      </article>

      <div className="mb-10">
        <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3 inline-flex items-center gap-1.5"><Film className="w-3 h-3" /> / Companion Video (placeholder)</div>
        <VideoCard slug={`learning-${article.slug}`} title={`${article.title} — Video`} description="Companion video walkthrough. Coming soon." provider={null} src={null} />
      </div>

      <div className="mb-10">
        <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ FAQ (placeholder)</div>
        <div className="space-y-3">
          {article.faq_placeholder.map((f, i) => (
            <details key={i} className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-4">
              <summary className="cursor-pointer font-display font-bold text-slate-100 text-sm">{f.q}</summary>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mb-10">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Related Topics</div>
          <div className="grid sm:grid-cols-2 gap-px bg-[var(--border-soft)]">
            {related.map((r) => (
              <Link key={r.slug} to={`/learning/${r.slug}`} className="bg-[var(--bg-secondary)] p-3 hover:bg-slate-900/70 group" data-testid={`learning-related-${r.slug}`}>
                <div className="font-display font-bold text-slate-100 text-sm group-hover:text-sky-300">{r.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.category}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {next && (
        <Link to={`/learning/${next.slug}`} data-testid="learning-next" className="block border border-sky-500/30 bg-sky-500/5 p-5 hover:bg-sky-500/10 transition-colors">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-1">/ Suggested Next Lesson</div>
          <div className="flex items-center justify-between gap-3">
            <div className="font-display font-bold text-slate-100 text-base">{next.title}</div>
            <ArrowRight className="w-4 h-4 text-sky-400 shrink-0" />
          </div>
        </Link>
      )}
    </div>
  );
}
